import { csrfToken } from '@/lib/csrf';

export class ApiError extends Error {
  readonly status: number;
  readonly detail?: string;
  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /**
   * When true (default) the response is parsed as JSON. When false the raw
   * Response object is returned (used for PDF blob fetch).
   */
  parseJson?: boolean;
}

const API_BASE = '/api/v1';

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value == null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  if (method !== 'GET') {
    const csrf = csrfToken();
    if (csrf) headers['x-csrf-token'] = csrf;
  }

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    body,
    credentials: 'include',
    signal: options.signal,
  });

  if (options.parseJson === false) {
    if (!response.ok) throw await errorFromResponse(response);
    return response as unknown as T;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const data: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    const detail = isRecord(data) && typeof data.detail === 'string' ? data.detail : undefined;
    throw new ApiError(response.status, detail ?? response.statusText, detail);
  }
  return data as T;
}

export async function errorFromResponse(res: Response): Promise<ApiError> {
  try {
    const text = await res.text();
    const data: unknown = text ? safeJson(text) : null;
    const detail = isRecord(data) && typeof data.detail === 'string' ? data.detail : undefined;
    return new ApiError(res.status, detail ?? res.statusText, detail);
  } catch {
    return new ApiError(res.status, res.statusText);
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
